import { MaintenanceStateInfo } from '@mp/system/maintenance/domain';

import { msUntilNextTransition } from './ms-until-next-transition';
import { parseUtcTimestamp } from './parse-utc-timestamp';

/**
 * The tick interval of the lock and of an expiring countdown.
 */
export const maintenanceFastTickIntervalMs = 1_000;

/**
 * The tick interval while the next transition is still far away.
 *
 * Deckungsgleich mit der Auflösung, die `formatCountdown` oberhalb der Schwelle liefert (Minuten): ein
 * schnellerer Takt erzeugte denselben String noch einmal und stiesse mit Zone.js trotzdem eine
 * anwendungsweite Change Detection an, pro offenem Tab über die ganze Vorlaufzeit der Ankündigung.
 */
export const maintenanceCoarseTickIntervalMs = 60_000;

/**
 * The remaining time from which on the countdown is ticked once a second.
 */
export const maintenanceFastTickThresholdMs = 3_600_000;

/**
 * Returns the interval the maintenance countdown has to be ticked with.
 *
 * Angekündigt wird üblicherweise Tage im Voraus, und "sofort ankündigen" ist der Standard: ein Sekundentakt
 * würde in jedem offenen Tab den ganzen Vorlauf über laufen - für einen Countdown, der `72:00:00` anzeigt und
 * dessen Sekundenstelle niemand sieht. Sekundengenau wird deshalb nur getaktet, wenn die Umschaltzeit nahe
 * ist oder die Oberfläche gesperrt ist; sonst gilt der grobe Takt, und der Countdown bleibt in seiner
 * Auflösung richtig, nur eben seltener aktualisiert.
 *
 * Gerechnet wird mit der Serverzeit der Antwort und nicht mit dem laufenden Takt: die Taktung darf nicht von
 * der Uhr abhängen, die sie steuert. Der Zustand wird mindestens alle fünf Minuten geholt, die Schwelle liegt
 * bei einer Stunde - der Wechsel in den Sekundentakt kommt also lange vor der Umschaltzeit.
 * @param stateInfo The last known maintenance state, `null` while it has not been loaded.
 * @param isLockRequired Whether the lock is currently shown.
 * @returns The tick interval in milliseconds.
 */
export function maintenanceTickIntervalMs(stateInfo: MaintenanceStateInfo | null, isLockRequired: boolean): number {
  if (isLockRequired) {
    return maintenanceFastTickIntervalMs;
  }

  if (stateInfo === null) {
    return maintenanceCoarseTickIntervalMs;
  }

  const serverNow = parseUtcTimestamp(stateInfo.serverUtcNow) ?? Date.now();
  const untilTransition = msUntilNextTransition(stateInfo, serverNow);

  return untilTransition !== null && untilTransition <= maintenanceFastTickThresholdMs
    ? maintenanceFastTickIntervalMs
    : maintenanceCoarseTickIntervalMs;
}
